import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faMinus, faPlus, faUser } from "@fortawesome/pro-solid-svg-icons";

import { StatusCode } from "api/protocols";
import { GetBooking } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { PostIssueRainCheck, PutCalculateRainCheck } from "api/rpc/2024-04/facilityAdmin/payment/rainCheck";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { IBookingParticipant, ISlot } from "redux/reducers/models/teetime";
import { convertHoles } from "helpers/Helpers";

import Sheet from "components/sheet/Sheet";
import Input from "components/form/input";
import { ButtonNew as Button } from "components/buttonNew";
import { Badge } from "components/badge/Badge";
import Callout from "components/callout/Callout";
import Checkbox from "components/form/checkbox/Checkbox";
import DataTable from "components/dataTable/DataTable";
import RainCheckCompletePopup from "components/rainCheckCompletePopup/RainCheckCompletePopup";
import Spin from "components/spin/spin";

interface IIssueRainCheckModalProps {
  open: boolean;
  /** Slots of the tee time. Used to pick a participant, and to avoid refetching a known one. */
  slots?: ISlot[];
  /** Course the tee time is on, so holes can be converted for 12 and 24 hole courses. */
  courseId?: number;
  /** When set, participant selection is skipped and the rain check is issued for this participant. */
  bookingParticipantId?: number;
  /** Only needed when the participant is not part of `slots`. */
  bookingId?: number;
  /** Booking holes, used when the participant is not part of `slots`. */
  holes?: number;
  onOk: () => void;
  onCancel: () => void;
}

type TRainCheckStep = "player-selection" | "amount-setting";

interface IRainCheckModalState {
  loading: boolean;
  step: TRainCheckStep;
  selectedBookingParticipant: IBookingParticipant | null;
  holes: number;
  completedHoles: string;
  percent: number | null;
  amount: number | null;
  calculate: number | null;
  orderTotal: number | null;
  orderId: number | null;
  issue_on_green_fee: boolean;
  issue_on_power_cart: boolean;
  okDisabled: boolean;
  rainCheckCompleteActive: boolean;
  rainCheck: any;
}

const initialState: IRainCheckModalState = {
  loading: true,
  step: "player-selection",
  selectedBookingParticipant: null,
  holes: 0,
  completedHoles: "",
  percent: null,
  amount: null,
  calculate: null,
  orderTotal: null,
  orderId: null,
  issue_on_green_fee: false,
  issue_on_power_cart: false,
  okDisabled: true,
  rainCheckCompleteActive: false,
  rainCheck: null,
};

function isEligible(slot: ISlot) {
  return (
    slot?.booking_participant?.check_in_status === "checked" &&
    slot?.booking_participant?.order_financial_status === "paid"
  );
}

export default function IssueRainCheckModal(props: IIssueRainCheckModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const { open, slots, courseId, bookingParticipantId, bookingId, holes, onOk, onCancel } = props;

  const [state, setState] = useState<IRainCheckModalState>(initialState);

  const selectionMode = bookingParticipantId == null;
  const eligibleSlots = slots?.filter(isEligible) ?? [];
  const course = facilityStore?.facility?.courses?.find(facilityCourse => facilityCourse.id === courseId);

  useEffect(() => {
    if (!open) {
      return;
    }

    void initialize();
  }, [open, bookingId, bookingParticipantId]);

  useEffect(() => {
    if (!open || state.step !== "amount-setting") {
      return;
    }

    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          try {
            if ((state.completedHoles === "" && state.amount === null) || state.orderId === null) {
              if (mounted) {
                setState(prev => ({ ...prev, calculate: null }));
              }
              return;
            }

            const calcRes = await PutCalculateRainCheck(
              {
                order_id: state.orderId,
                booking_participant_id: state.selectedBookingParticipant?.id,
                issue_on_green_fee: state.issue_on_green_fee,
                issue_on_power_cart: state.issue_on_power_cart,
                amount: state.amount,
                percent: state.percent,
              },
              true,
            );

            if (calcRes.status !== StatusCode.OK) {
              return;
            }

            if (mounted) {
              setState(prev => ({ ...prev, calculate: calcRes.data, okDisabled: false }));
            }
          } catch (error) {
            console.log("err", error);
          }
        })();
      }, 1000);
    };

    search();

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [state.percent, state.amount, state.step, open]);

  async function initialize() {
    if (selectionMode) {
      setState({ ...initialState, loading: false, step: "player-selection" });
      return;
    }

    const slotParticipant = slots?.find(slot => slot?.booking_participant?.id === bookingParticipantId);

    if (slotParticipant) {
      setState({ ...initialState, loading: false, step: "amount-setting" });
      await selectParticipant(slotParticipant.booking_participant, slotParticipant.booking?.holes);
      return;
    }

    setState({ ...initialState, loading: true, step: "amount-setting" });

    const bookingRes = await GetBooking({ id: bookingId, extended: true }, true);

    if (bookingRes.status !== StatusCode.OK || !bookingRes.data?.[0]) {
      dispatch(showError(typeof bookingRes?.data === "string" ? bookingRes.data : t("Error getting booking")));
      onCancel();
      return;
    }

    const booking = bookingRes.data[0];
    const participant = booking.participants?.find((p: IBookingParticipant) => p.id === bookingParticipantId);

    setState(prev => ({ ...prev, loading: false }));
    await selectParticipant(participant, booking.holes);
  }

  /** Loads everything needed to price a rain check for one participant. */
  async function selectParticipant(participant: IBookingParticipant | undefined, bookingHoles: number | undefined) {
    if (!participant || participant.order_id === null) {
      dispatch(showError(t("Error getting booking")));
      onCancel();
      return;
    }

    const orderId = participant.order_id;
    const issueOnGreenFee = participant.green_fee_variant_id !== null && participant.green_fee_paid;
    const issueOnPowerCart = participant.power_cart_variant_id !== null && participant.power_cart_paid;

    setState(prev => ({
      ...prev,
      selectedBookingParticipant: participant,
      holes: convertHoles(bookingHoles ?? holes ?? 0, course),
      orderId,
      issue_on_green_fee: issueOnGreenFee,
      issue_on_power_cart: issueOnPowerCart,
      completedHoles: "",
      percent: null,
      amount: null,
      calculate: null,
      orderTotal: null,
    }));

    // A rain check for zero completed holes refunds the full amount, so the same
    // endpoint that prices the rain check also gives the order total it is based on.
    const totalRes = await PutCalculateRainCheck(
      {
        order_id: orderId,
        booking_participant_id: participant.id,
        issue_on_green_fee: issueOnGreenFee,
        issue_on_power_cart: issueOnPowerCart,
        amount: null,
        percent: 1,
      },
      false,
    );

    setState(prev =>
      prev.selectedBookingParticipant?.id === participant.id
        ? { ...prev, orderTotal: totalRes.status === StatusCode.OK ? Number(totalRes.data) || 0 : null }
        : prev,
    );
  }

  function handleRainCheckHolesInput(event: React.ChangeEvent<HTMLInputElement>) {
    const { id } = event.target;
    let { value } = event.target;
    let percentValue: number;

    if (value !== "") {
      value = Math.min(state.holes, Number(value)).toString();
      percentValue = 1 - Number(value) / state.holes;
      setState(prev => ({ ...prev, [id]: value, percent: percentValue }));
      return;
    }

    setState(prev => ({ ...prev, [id]: value, percent: null }));
  }

  function handleRainCheckAmountInput(event: React.ChangeEvent<HTMLInputElement>) {
    const { id } = event.target;
    const value = event.target.value === "" ? null : Number(event.target.value);
    setState(prev => ({ ...prev, [id]: value }));
  }

  function handleIncrementHoles() {
    setState(prev => {
      const newCompletedHoles = Math.min(Number(prev.completedHoles || 0) + 1, prev.holes);
      const percentValue = 1 - newCompletedHoles / prev.holes;

      return {
        ...prev,
        completedHoles: newCompletedHoles.toString(),
        percent: percentValue,
      };
    });
  }

  function handleDecrementHoles() {
    setState(prev => {
      const newCompletedHoles = Math.max(Number(prev.completedHoles || 0) - 1, 0);
      const percentValue = newCompletedHoles === 0 ? null : 1 - newCompletedHoles / prev.holes;

      return {
        ...prev,
        completedHoles: newCompletedHoles.toString(),
        percent: percentValue,
      };
    });
  }

  async function issueRainCheck() {
    const rainCheckRes = await PostIssueRainCheck(
      {
        order_id: state.orderId,
        booking_participant_id: state.selectedBookingParticipant?.id,
        issue_on_green_fee: state.issue_on_green_fee,
        issue_on_power_cart: state.issue_on_power_cart,
        amount: state.calculate,
      },
      true,
    );

    if (rainCheckRes.status !== StatusCode.OK) {
      dispatch(showError(rainCheckRes.data as string));
      return;
    }

    dispatch(showSuccess(t("secure.facility.tee_sheet.tee_sheet_booking_details.209")));
    setState(prev => ({
      ...prev,
      rainCheck: rainCheckRes.data,
      rainCheckCompleteActive: true,
    }));
  }

  function handleOk() {
    if (state.step === "player-selection") {
      setState(prev => ({
        ...prev,
        step: "amount-setting",
        okDisabled: true,
        completedHoles: "",
        percent: null,
        amount: null,
        calculate: null,
      }));
      return;
    }

    void issueRainCheck();
  }

  function handleCancel() {
    setState(initialState);
    onCancel();
  }

  function handleBack() {
    setState(prev => ({ ...prev, step: "player-selection", okDisabled: true }));
  }

  function handleCompleteClose() {
    setState(initialState);
    onOk();
  }

  const okDisabled =
    state.loading ||
    !state.selectedBookingParticipant ||
    (state.step === "amount-setting" && state.okDisabled);

  const showBack = selectionMode && state.step === "amount-setting";

  return (
    <>
      <Sheet
        open={open && !state.rainCheckCompleteActive}
        size="small"
        closable
        title={
          state.step === "amount-setting" ? (
            <>
              Issue rain check for:{" "}
              <Badge size="small" type="gray">
                <FontAwesomeIcon icon={faUser} className="mr-1" />
                {state.selectedBookingParticipant?.full_name}
              </Badge>
            </>
          ) : (
            t("Rain Check")
          )
        }
        onCancel={handleCancel}
        onOk={handleOk}
        onSecondary={showBack ? handleBack : undefined}
        secondaryText={showBack ? t("Back") : undefined}
        cancelText={t("Cancel")}
        okText={state.step === "player-selection" ? t("Continue") : t("Issue rain check")}
        okDisabled={okDisabled}
      >
        {state.loading ? (
          <div className="flex justify-center p-4">
            <Spin />
          </div>
        ) : state.step === "player-selection" ? (
          <div>
            {eligibleSlots.length > 0 ? (
              <DataTable
                columns={[
                  { label: t("player-Selection"), width: "5%" },
                  { label: t("Player Name"), width: "95%" },
                ]}
              >
                {eligibleSlots.map(slot => (
                  <tr
                    key={slot.id}
                    className="clickable"
                    onClick={() => void selectParticipant(slot.booking_participant, slot.booking?.holes)}
                  >
                    <td>
                      <Checkbox
                        size="small"
                        checked={state.selectedBookingParticipant?.id === slot.booking_participant?.id}
                        readOnly
                      />
                    </td>
                    <td>{slot?.booking_participant?.full_name}</td>
                  </tr>
                ))}
              </DataTable>
            ) : (
              <Callout
                type="info"
                title={t("No Paid Fees")}
                content={t("Complete a check in on this booking to create a rain check")}
              />
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-subdued text-sm mb-1">
                Your order total:{" "}
                <span className="font-semibold text-black">${(state.orderTotal ?? 0).toFixed(2)}</span>
              </p>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Holes completed</p>
              <Input
                id="completedHoles"
                value={state.completedHoles}
                type="number"
                onChange={handleRainCheckHolesInput}
                trailingButtons={[
                  <Button
                    key="decrement"
                    onClick={handleDecrementHoles}
                    disabled={!state.completedHoles || Number(state.completedHoles) <= 0}
                    size="small"
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </Button>,
                  <Button
                    key="increment"
                    onClick={handleIncrementHoles}
                    disabled={Number(state.completedHoles) >= state.holes}
                    size="small"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>,
                ]}
              />
              <p className="text-xs text-subdued mt-1">Total holes ranges from 0 - {state.holes}.</p>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center mb-3">
                <p className="text-sm font-medium mr-2">Rain check amount:</p>
                <p className="font-semibold text-green-600">
                  ${typeof state.calculate === "object" ? "0.00" : (state.calculate ?? 0).toFixed(2)}
                </p>
              </div>

              <details className="group">
                <summary className="flex items-center cursor-pointer list-none">
                  <span className="text-sm font-medium mr-2">Custom amount</span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </summary>

                <div className="mt-3">
                  <p className="text-xs text-subdued mb-2">Enter custom amount</p>
                  <Input
                    value={state.amount || ""}
                    type="number"
                    id="amount"
                    onChange={handleRainCheckAmountInput}
                    placeholder="Enter amount"
                    prefix="$"
                  />
                </div>
              </details>
            </div>
          </div>
        )}
      </Sheet>

      <RainCheckCompletePopup
        open={state.rainCheckCompleteActive}
        setClose={handleCompleteClose}
        rainCheck={state.rainCheck}
        bookingParticipant={state.selectedBookingParticipant}
        rainCheckState={state}
      />
    </>
  );
}
